<template>
  <component :is="tag" ref="wrapperRef" data-test-id="base-popover" @mouseleave="onMouseleave">
    <slot v-bind="{ opened, side, toggle, open, close }" />
  </component>
</template>

<script lang="ts" setup>
import type { Props } from '#types/components/base/popover'
import type { Side } from '@floating-ui/vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { PopoverContextKey } from './context'

const {
  tag = 'div',
  placement = 'bottom',
  arrowShift = 0,
  destroy = true,
  delay,
  ...props
} = defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

defineSlots<{
  default: (props: {
    opened: boolean
    side: Side
    toggle: typeof toggle
    open: typeof open
    close: typeof close
  }) => any
}>()

const opened = defineModel({ default: false })
const initiator = ref()
const floating = ref()
const wrapperRef = ref<HTMLDivElement>()
const side = ref(placement.split('-')[0] as Side)
const { show, hide } = isObject(delay) ? delay : { show: delay, hide: delay }
let timeout

const handler = (cb: (e?: Event) => void, delay?: number) => (e?: Event) => {
  clearTimeout(timeout)
  if (!delay) return cb(e)
  timeout = setTimeout(() => cb(e), delay)
}

const forceOpen = (e) => {
  initiator.value = e?.target
  opened.value = true
}
const open = handler(forceOpen, show)

const forceClose = () => {
  opened.value = false
  emit('close')
}

const close = handler(forceClose, hide)

const toggle = (e: Event) => (opened.value ? forceClose() : forceOpen(e))

provide(PopoverContextKey, {
  opened,
  initiator,
  floating,
  side,
  forceClose,
  placement: computed(() => placement),
  arrowShift: computed(() => arrowShift),
  destroy: computed(() => destroy),
  ...toRefs(props)
})

const nonInteractiveHandlers = {
  activate: () => forceOpen(event),
  deactivate: () => forceClose()
}

const { activate, deactivate } = props.interactive
  ? useFocusTrap(wrapperRef, { initialFocus: false })
  : nonInteractiveHandlers

watch(opened, () => {
  opened.value ? activate() : deactivate()
})

const onMouseleave = (e) => {
  if (!props.closeOnLeave || e.toElement === floating.value) return
  if (containsChildElement(floating.value, e.toElement)) return
  forceClose()
}

defineExpose({
  open,
  close,
  opened
})
</script>
